/* eslint-disable no-void */

"use client"

/* eslint-disable no-nested-ternary */
import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import moment from "moment"
import Image from "next/image"
import Link from "next/link"
import React, { useCallback, useEffect, useRef, useState } from "react"

import ArrowCarouselDark from "../../../public/icons/arrow-carousel-dark.svg"
import ArrowCarousel from "../../../public/icons/arrow-carousel.svg"

export interface CarouselProps {
  date: string
  title: string
  seo: {
    featuredImage: {
      url: string
      title?: string
    }
  }
  slug: string
  lg: boolean
}

interface BPProps {
  wrapperClass?: string
  textColor?: string
  secondaryTextColor?: string
  darkMode?: boolean
  data: CarouselProps[]
  header?: string
  strokeClassname?: string
  newTab?: boolean
}

const SelectPostsCarousel: React.FC<BPProps> = ({
  data,
  wrapperClass,
  textColor,
  secondaryTextColor,
  header,
  darkMode = true,
  strokeClassname = "",
  newTab = false,
}) => {
  const divRef = useRef<HTMLInputElement>(null)
  const [leftMargin, setLeftMargin] = React.useState(32)

  // Simple Embla setup with responsive options
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
    breakpoints: {
      "(min-width: 768px)": { slidesToScroll: 1 },
      "(min-width: 1024px)": { slidesToScroll: 1 },
    },
  })

  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((api: any) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  useEffect(() => {
    function handleResize() {
      const offset = divRef.current?.offsetLeft
      // eslint-disable-next-line no-void, no-unused-expressions
      offset !== undefined && void setLeftMargin(offset)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  })

  const Arrow = darkMode ? ArrowCarousel : ArrowCarouselDark

  return (
    <div className={wrapperClass}>
      <div
        className="mx-4 lg:max-w-layout-lg lg:mx-auto xl:max-w-layout-xl"
        ref={divRef}
      >
        {header && (
          <p
            className={clsx(
              "block p-4 mb-6 md:mb-24 heading1 md:headline5 lg:headline4",
              textColor,
            )}
          >
            {header}
          </p>
        )}
      </div>
      <div className="pl-4 pr-8 md:pr-0" style={{ marginLeft: leftMargin }}>
        {/* Embla Carousel with CSS-based responsive sizing */}
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {data?.map(({ date, title, seo, slug, lg }) => {
              const link = lg ? `/lp/${slug}` : `/insights/${slug}`
              const image = seo?.featuredImage?.url
              const formattedDate = moment.utc(date).format("MMMM D, YYYY")
              return (
                <div
                  key={slug}
                  className="embla__slide flex-[0_0_85%] min-w-0 pr-4 md:pr-8 md:flex-[0_0_42%] lg:flex-[0_0_33%] xl:flex-[0_0_28%]"
                >
                  <Link
                    href={link}
                    target={newTab ? "_blank" : "_self"}
                    className="text-blue-45 block"
                  >
                    <div className="mb-6 relative w-full h-0 pb-[56.25%]">
                      <Image
                        className="absolute top-0 left-0 w-full h-full rounded-lg object-cover"
                        src={image}
                        alt={
                          seo?.featuredImage?.title ??
                          "Featured Blog Post Image"
                        }
                        layout="fill"
                      />
                    </div>
                    <article className="py-2">
                      <p className={clsx("heading2 mb-4", textColor)}>
                        {title}
                      </p>
                      <div className="flex items-center">
                        <span className={clsx("caption1", secondaryTextColor)}>
                          {formattedDate}
                        </span>
                      </div>
                    </article>
                  </Link>
                </div>
              )
            })}
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="flex pl-4 mt-10 md:mt-4">
          <button
            type="button"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className={`${strokeClassname} disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50`}
          >
            <Arrow className="mr-10 transform -rotate-180 h-14 w-14" />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            disabled={!canScrollNext}
            className={`disabled:opacity-50 ${strokeClassname} disabled:cursor-not-allowed hover:opacity-50`}
          >
            <Arrow className="h-14 w-14" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default SelectPostsCarousel
